import { isPageNotAudio, isPlayableUrl, repairAudioUrl } from "./share.ts";
import { soundcloudTrack } from "./soundcloud.ts";
import { isSunoLink, resolveSuno, type SunoTrack, sunoSongId } from "./suno.ts";
import { youtubeId } from "./youtube.ts";

/**
 * A track, from wherever it is kept.
 *
 * Suno was never special. It happens to serve its files with the door open - `audio/mpeg`,
 * range requests, `access-control-allow-origin: *` - and that is the whole requirement, because
 * a browser has to be allowed to *read* a file to analyse it and not merely to play it. Anywhere
 * else that leaves the door open works exactly as well, so the gate is now the property rather
 * than the hostname.
 *
 * What is still refused is a link to a *page* about music. That is the likeliest mistake by a
 * distance and the worst one to allow through, because a media element pointed at HTML fires no
 * error at all: it simply never plays, and the failure looks like the app being broken.
 */

export type TrackSource = "suno" | "direct";

export interface Track extends SunoTrack {
  readonly source: TrackSource;
}

/** Anything this can turn into a game, which is a Suno link or an address of a file. */
export function isTrackLink(value: string): boolean {
  const trimmed = value.trim();
  if (isSunoLink(trimmed)) return true;
  // Neither of these can be read, only listened to, which is a different path rather than a no.
  if (youtubeId(trimmed) !== null) return true;
  if (soundcloudTrack(trimmed) !== null) return true;
  return isPlayableUrl(trimmed) && !isPageNotAudio(trimmed);
}

/**
 * A name for a track that is the same every time, so the second person to paste a link is sent
 * to the game the first one made rather than making a second copy of it.
 *
 * Suno tracks keep the id Suno gave them. Anything else is named by its own address, hashed to
 * the shape the store already files games under, so nothing downstream has to learn a second
 * kind of name.
 */
export function trackId(audioUrl: string): string {
  const known = sunoSongId(audioUrl);
  if (known !== null) return known;

  // Four rounds of FNV-1a over the address, each salted differently, for 128 bits of name.
  const parts: string[] = [];
  for (let round = 0; round < 4; round++) {
    let hash = 2166136261 ^ (round * 0x9e3779b1);
    for (let i = 0; i < audioUrl.length; i++) {
      hash ^= audioUrl.charCodeAt(i);
      hash = Math.imul(hash, 16777619);
    }
    parts.push((hash >>> 0).toString(16).padStart(8, "0"));
  }
  const flat = parts.join("");
  return `${flat.slice(0, 8)}-${flat.slice(8, 12)}-${flat.slice(12, 16)}-${flat.slice(16, 20)}-${flat.slice(20, 32)}`;
}

/**
 * Checks the door is actually open before a run at analysing it.
 *
 * Without this the failure arrives a long way from its cause: the fetch inside the analyser
 * throws a bare `TypeError` that says nothing about which of the several possible reasons it
 * was, having already spent a while looking like it was working. One request up front turns
 * that into a sentence somebody can act on.
 */
export async function checkReadable(audioUrl: string): Promise<string | null> {
  let answer: Response;
  try {
    answer = await fetch(audioUrl, { method: "GET", headers: { range: "bytes=0-1" } });
  } catch {
    return (
      "That address will not let this read the file. Wherever it is kept has to allow other " +
      "sites to fetch it, which is what lets a track be listened to without being downloaded."
    );
  }
  if (!answer.ok && answer.status !== 206) {
    return answer.status === 404
      ? "There is nothing at that address."
      : `Whatever is at that address answered ${answer.status}.`;
  }
  const kind = (answer.headers.get("content-type") ?? "").toLowerCase();
  if (kind.startsWith("text/") || kind.includes("html")) {
    return "That address holds a page rather than a track. A browser cannot play a web page.";
  }
  return null;
}

export async function resolveTrack(value: string): Promise<Track> {
  const trimmed = value.trim();
  if (isSunoLink(trimmed)) return { ...(await resolveSuno(trimmed)), source: "suno" };

  if (isPageNotAudio(trimmed)) {
    throw new Error(
      "That is a page about music rather than a track. Paste the address of the audio itself.",
    );
  }
  if (!isPlayableUrl(trimmed)) throw new Error("That does not look like a link to a track.");

  const audio = repairAudioUrl(trimmed);
  const refused = await checkReadable(audio);
  if (refused !== null) throw new Error(refused);

  // The last part of the path is the only name a bare file has.
  let title = "";
  try {
    const guess = decodeURIComponent(new URL(audio).pathname.split("/").pop() ?? "");
    title = guess
      .replace(/\.[a-z0-9]{2,4}$/i, "")
      .replace(/[_-]+/g, " ")
      .trim();
  } catch {
    title = "";
  }
  return { audio, title: title.slice(0, 80), source: "direct" };
}
